<script>
import { mapActions } from 'vuex';
import request from '@/services/request';

export default {
    data() {
        return {
            loading: false,
            saving: false,
            errors: {},

            depositItem: null,
            existingReturn: null,

            form: {
                return_date: '',
                comments: '',
                items: [],
            }
        }
    },

    computed: {
        depositAmount() {
            if (!this.depositItem) {
                return 0;
            }

            return Number(this.depositItem.final_amount || 0);
        },

        deductionsTotal() {
            return this.form.items.reduce((total, item) => {
                return total + this.itemAmount(item);
            }, 0);
        },

        amountReturned() {
            let amount = this.depositAmount - this.deductionsTotal;

            return amount > 0 ? amount : 0;
        },

        amountPending() {
            let amount = this.depositAmount - this.deductionsTotal;

            return amount < 0 ? Math.abs(amount) : 0;
        },

        resultText() {
            if (this.amountPending > 0) {
                return 'Los descuentos superan el depósito. No se regresa dinero y queda un saldo pendiente por cobrar.';
            }

            if (this.deductionsTotal <= 0) {
                return 'No hay descuentos. Se regresa el depósito completo.';
            }

            if (this.amountReturned <= 0) {
                return 'Los descuentos consumen todo el depósito. No se regresa dinero.';
            }

            return 'Se regresará una parte del depósito después de aplicar los descuentos.';
        },

        resultAlertClass() {
            if (this.amountPending > 0) {
                return 'alert-danger';
            }

            if (this.deductionsTotal <= 0) {
                return 'alert-success';
            }

            if (this.amountReturned <= 0) {
                return 'alert-warning';
            }

            return 'alert-info';
        }
    },

    created() {
        Event.$on("open-deposit-return-modal", (deposit = {}) => {
            this.resetForm();
            console.log(deposit);

            if (!deposit || !deposit.id) {
                this.warningNotification('No se encontró el depósito.');
                return;
            }

            this.$modal.show('depositReturnModal');

            this.loadDepositReturn(deposit.id);
        });
    },

    beforeDestroy() {
        Event.$off("event-show-deposit-return");
    },

    methods: {
        resetForm() {
            this.loading = false;
            this.saving = false;
            this.errors = {};

            this.depositItem = null;
            this.existingReturn = null;

            this.form = {
                return_date: '',
                comments: '',
                items: [],
            };
        },

        closeModal() {
            this.resetForm();
            this.$modal.hide('depositReturnModal');
        },

        async loadDepositReturn(contractDueItemId) {
            this.loading = true;
            this.errors = {};

            try {
                let { data } = await request.get(`/deposit-returns/deposit-item/${contractDueItemId}`);

                this.depositItem = data.deposit || null;
                this.existingReturn = data.return || null;

                if (this.existingReturn) {
                    this.form.return_date = this.existingReturn.return_date || '';
                    this.form.comments = this.existingReturn.comments || '';
                    this.form.items = this.existingReturn.items || [];
                } else {
                    this.form.return_date = this.today();
                    this.form.comments = '';
                    this.form.items = [];
                }

            } catch (error) {
                if (error.response) {
                    this.errors = await this.errorNotification(error);
                }

                this.closeModal();
            } finally {
                this.loading = false;
            }
        },

        addItem() {
            this.form.items.push({
                concept: '',
                description: '',
                quantity: 1,
                unit_price: 0,
            });
        },

        removeItem(index) {
            this.form.items.splice(index, 1);
        },

        itemAmount(item) {
            let quantity = Number(item.quantity || 0);
            let unitPrice = Number(item.unit_price || 0);

            return Number((quantity * unitPrice).toFixed(2));
        },

        validateItems() {
            this.errors = {};

            let invalidItem = this.form.items.find(item => {
                return !item.concept ||
                    Number(item.quantity || 0) <= 0 ||
                    Number(item.unit_price || 0) < 0;
            });

            if (invalidItem) {
                this.warningNotification('Revisa los conceptos, cantidades y precios de los descuentos.');
                return false;
            }

            return true;
        },

        async saveDepositReturn() {
            if (!this.depositItem || !this.depositItem.id) {
                this.warningNotification('No se encontró el depósito.');
                return;
            }

            if (!this.validateItems()) {
                return;
            }

            this.saving = true;
            this.errors = {};

            let form = {
                return_date: this.form.return_date,
                comments: this.form.comments,
                items: this.form.items,
            };

            try {
                let { data } = await request.postMedia(`/deposit-returns/deposit-item/${this.depositItem.id}`, form);

                if (data) {
                    this.successNotification(data.message || 'Devolución registrada correctamente.');

                    this.closeModal();

                    Event.$emit('event-load-deposits');
                    Event.$emit('event-deposit-return-saved', data.data);
                }

            } catch (error) {
                this.errors = {};

                if (error.response) {
                    this.errors = await this.errorNotification(error);
                }
            } finally {
                this.saving = false;
            }
        },

        today() {
            let date = new Date();
            let month = String(date.getMonth() + 1).padStart(2, '0');
            let day = String(date.getDate()).padStart(2, '0');

            return `${date.getFullYear()}-${month}-${day}`;
        },

        money(value) {
            let amount = Number(value || 0);

            return amount.toLocaleString('es-MX', {
                style: 'currency',
                currency: 'MXN'
            });
        },

        ...mapActions({
            errorNotification: 'alert/error',
            successNotification: 'alert/success',
            warningNotification: 'alert/warning',
        }),
    }
}
</script>

<template>
    <modal v-bind:click-to-close="false" name="depositReturnModal" :width="'90%'" :height="'auto'">
        <form @submit.prevent="saveDepositReturn">
            <button type="button" class="btn btn-danger btn-sm btn-closed" @click="closeModal()">
                <i class="fa-solid fa-xmark"></i>
            </button>

            <div class="modal-header">
                <h5 class="modal-title">
                    Regresar depósito
                </h5>
            </div>

            <div class="modal-body">
                <div v-if="loading" class="text-center p-4">
                    <i class="fas fa-spinner fa-spin"></i>
                    Cargando información...
                </div>

                <div v-else>
                    <div class="row mb-3">
                        <div class="col-md-3">
                            <div class="info-box bg-light">
                                <span class="info-box-icon bg-info">
                                    <i class="fas fa-hand-holding-usd"></i>
                                </span>

                                <div class="info-box-content">
                                    <span class="info-box-text">Depósito original</span>
                                    <span class="info-box-number">
                                        {{ money(depositAmount) }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="col-md-3">
                            <div class="info-box bg-light">
                                <span class="info-box-icon bg-warning">
                                    <i class="fas fa-tools"></i>
                                </span>

                                <div class="info-box-content">
                                    <span class="info-box-text">Descuentos</span>
                                    <span class="info-box-number">
                                        {{ money(deductionsTotal) }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="col-md-3">
                            <div class="info-box bg-light">
                                <span class="info-box-icon bg-success">
                                    <i class="fas fa-money-bill-wave"></i>
                                </span>

                                <div class="info-box-content">
                                    <span class="info-box-text">Monto a regresar</span>
                                    <span class="info-box-number">
                                        {{ money(amountReturned) }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="col-md-3">
                            <div class="info-box bg-light">
                                <span class="info-box-icon bg-danger">
                                    <i class="fas fa-exclamation-triangle"></i>
                                </span>

                                <div class="info-box-content">
                                    <span class="info-box-text">Saldo pendiente</span>
                                    <span class="info-box-number">
                                        {{ money(amountPending) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="existingReturn" class="alert alert-info">
                        Este depósito ya tiene una devolución registrada con estatus:
                        <strong>{{ existingReturn.status }}</strong>
                    </div>

                    <div class="row">
                        <div class="col-md-4">
                            <label>Fecha de devolución</label>

                            <input
                                type="date"
                                class="form-control"
                                v-model="form.return_date"
                                :disabled="!!existingReturn"
                            >

                            <div v-if="errors && errors.return_date" class="text-danger">
                                {{ errors.return_date[0] }}
                            </div>
                        </div>

                        <div class="col-md-8">
                            <label>Comentarios generales</label>

                            <input
                                type="text"
                                class="form-control"
                                v-model="form.comments"
                                placeholder="Ejemplo: Se descuentan daños detectados al entregar el inmueble"
                                :disabled="!!existingReturn"
                            >

                            <div v-if="errors && errors.comments" class="text-danger">
                                {{ errors.comments[0] }}
                            </div>
                        </div>
                    </div>

                    <hr>

                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <h5 class="mb-0">
                            Descuentos del depósito
                        </h5>

                        <button
                            type="button"
                            class="btn btn-sm btn-outline-primary"
                            @click="addItem"
                            :disabled="!!existingReturn"
                        >
                            <i class="fas fa-plus"></i>
                            Agregar descuento
                        </button>
                    </div>

                    <div class="table-responsive">
                        <table class="table table-bordered table-sm">
                            <thead class="thead-light">
                                <tr>
                                    <th style="width: 25%;">Concepto</th>
                                    <th>Descripción</th>
                                    <th style="width: 10%;">Cantidad</th>
                                    <th style="width: 15%;">Precio</th>
                                    <th style="width: 15%;">Importe</th>
                                    <th style="width: 60px;"></th>
                                </tr>
                            </thead>

                            <tbody>
                                <tr v-for="(item, index) in form.items" :key="index">
                                    <td>
                                        <input
                                            type="text"
                                            class="form-control form-control-sm"
                                            v-model="item.concept"
                                            placeholder="Vidrio roto"
                                            :disabled="!!existingReturn"
                                        >

                                        <div v-if="errors && errors[`items.${index}.concept`]" class="text-danger">
                                            {{ errors[`items.${index}.concept`][0] }}
                                        </div>
                                    </td>

                                    <td>
                                        <input
                                            type="text"
                                            class="form-control form-control-sm"
                                            v-model="item.description"
                                            placeholder="Detalle del daño"
                                            :disabled="!!existingReturn"
                                        >
                                    </td>

                                    <td>
                                        <input
                                            type="number"
                                            min="0.01"
                                            step="0.01"
                                            class="form-control form-control-sm text-right"
                                            v-model.number="item.quantity"
                                            :disabled="!!existingReturn"
                                        >
                                    </td>

                                    <td>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            class="form-control form-control-sm text-right"
                                            v-model.number="item.unit_price"
                                            :disabled="!!existingReturn"
                                        >
                                    </td>

                                    <td class="text-right align-middle">
                                        {{ money(itemAmount(item)) }}
                                    </td>

                                    <td class="text-center align-middle">
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-danger"
                                            @click="removeItem(index)"
                                            :disabled="!!existingReturn"
                                        >
                                            <i class="fas fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>

                                <tr v-if="form.items.length === 0">
                                    <td colspan="6" class="text-center text-muted">
                                        No hay descuentos agregados.
                                    </td>
                                </tr>
                            </tbody>

                            <tfoot>
                                <tr>
                                    <th colspan="4" class="text-right">
                                        Total descuentos
                                    </th>

                                    <th class="text-right">
                                        {{ money(deductionsTotal) }}
                                    </th>

                                    <th></th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    <div class="alert" :class="resultAlertClass">
                        <strong>Resultado:</strong>
                        {{ resultText }}
                    </div>

                    <div class="col-md-12 mb-55">
                        <hr>

                        <button
                            v-if="!existingReturn"
                            type="submit"
                            class="btn float-right btn-outline-success"
                            :disabled="saving"
                        >
                            <span v-if="saving">
                                <i class="fas fa-spinner fa-spin"></i>
                                Guardando...
                            </span>

                            <span v-else>
                                <i class="fas fa-save"></i>
                                Confirmar devolución
                            </span>
                        </button>

                        <button
                            type="button"
                            @click="closeModal()"
                            class="btn float-left btn-outline-warning"
                        >
                            <i class="fas fa-undo-alt"></i>
                            Regresar
                        </button>
                    </div>
                </div>
            </div>
        </form>
    </modal>
</template>
<style scoped>
.mb-55{
    margin-bottom: 55px;
}
</style>